/**
 * "Monthly stays" on /search (Maged, 2026-10-03: "choose the units with the
 * discounts for now"; renamed from "Long stay" to "Monthly stays" on Maged's
 * call 2026-10-15, superseding the earlier "never 'Monthly'" rule). Code and
 * URL keep the old `long` names: `stay=long` is a public, shared contract
 * (iOS app, saved links).
 *
 * The hero's Long stay toggle appends `stay=long` to the search URL. With it,
 * /search lists only published homes that actually give a long stay something:
 *
 *  1. a length-of-stay curve (`unit_los_discount`) that DISCOUNTS a stay of
 *     LONG_STAY_MIN_NIGHTS nights — judged with the same `losMultiplier()` the
 *     booking card prices with, so a unit qualifies exactly when a 30-night
 *     quote shows a "Long-stay discount" line. A curve that is all 1.0, or whose
 *     longest bucket at or under 30 nights is 1.0 (VESTA 95001 is 19% off at 7
 *     nights and full price from 14), does not qualify;
 *  2. or an operator monthly rate (`units.monthly_price_egp > 0`, Brain D-052).
 *
 * Pure and client-safe (no Supabase): wp.ts feeds it the raw rows, and the
 * Node test runner imports it directly — hence the `.ts` import below.
 */
import { losMultiplier, type LosCurve } from "./los-discount.ts";

/** How far ahead the default monthly window starts when the guest gave no dates. */
export const DEFAULT_MOVE_IN_DAYS = 7;

/** The hero's long-stay minimum — also the stay a curve is judged against. */
export const LONG_STAY_MIN_NIGHTS = 30;

/** URL key + value the hero SearchBar writes in long-stay mode. */
export const STAY_PARAM = "stay";
export const STAY_LONG = "long";

/** True only for `stay=long` (case/space tolerant); anything else is ignored. */
export function isLongStayParam(v: string | string[] | null | undefined): boolean {
  const s = Array.isArray(v) ? v[0] : v;
  return typeof s === "string" && s.trim().toLowerCase() === STAY_LONG;
}

/** Copy of `current` with `stay=long` set or removed. `page` is dropped — a
 *  changed filter starts at page 1, like every other filter on /search. */
export function withLongStay(current: URLSearchParams, on: boolean): URLSearchParams {
  const next = new URLSearchParams(current.toString());
  if (on) next.set(STAY_PARAM, STAY_LONG);
  else next.delete(STAY_PARAM);
  next.delete("page");
  return next;
}

export type LosRow = { wp_post_id: number | null; nights: number | string; multiplier: number | string };
export type MonthlyRow = { wp_post_id: number | null; monthly_price_egp: number | string | null };

export interface LongStaySet {
  /** Sorted, distinct wp_post_ids that qualify. */
  ids: number[];
  /** The curve of each id that qualifies through rule 1 (for the card label). */
  curves: Record<number, LosCurve>;
}

/** Which units qualify, from raw `unit_los_discount` rows and `units` rows. */
export function qualifyingLongStay(losRows: LosRow[], monthlyRows: MonthlyRow[]): LongStaySet {
  const byUnit = new Map<number, LosCurve>();
  for (const r of losRows) {
    const id = Number(r.wp_post_id);
    if (!Number.isFinite(id) || id <= 0) continue;
    const curve = byUnit.get(id) ?? {};
    curve[Number(r.nights)] = Number(r.multiplier);
    byUnit.set(id, curve);
  }
  const ids = new Set<number>();
  const curves: Record<number, LosCurve> = {};
  for (const [id, curve] of byUnit) {
    if (losMultiplier(curve, LONG_STAY_MIN_NIGHTS) < 1) {
      ids.add(id);
      curves[id] = curve;
    }
  }
  for (const r of monthlyRows) {
    const id = Number(r.wp_post_id);
    const monthly = Number(r.monthly_price_egp);
    if (Number.isFinite(id) && id > 0 && Number.isFinite(monthly) && monthly > 0) ids.add(id);
  }
  return { ids: [...ids].sort((a, b) => a - b), curves };
}

const addDaysIso = (iso: string, days: number): string =>
  new Date(Date.parse(`${iso}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
const isIsoDate = (v: string | undefined): v is string =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`));

/**
 * Monthly stays always price real dates (Maged, 2026-10-15): with no dates the
 * cards would have no month to total. Fills only what is missing — a move-in
 * a week from today (Cairo), and a move-out LONG_STAY_MIN_NIGHTS later — and
 * returns null when the guest already gave both dates (never overrides them).
 */
export function defaultMonthlyWindow(
  checkin: string | undefined,
  checkout: string | undefined,
  todayIso: string,
): { checkin: string; checkout: string } | null {
  const hasIn = isIsoDate(checkin);
  if (hasIn && isIsoDate(checkout)) return null;
  const start = hasIn ? checkin : addDaysIso(todayIso, DEFAULT_MOVE_IN_DAYS);
  return { checkin: start, checkout: addDaysIso(start, LONG_STAY_MIN_NIGHTS) };
}
